import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch } from '../../api';
import { useAuth } from '../../context/AuthContext';

const {
  FiUsers, FiPlus, FiTrash2, FiEdit3, FiX, FiShield,
  FiUser, FiMail, FiClock, FiSearch, FiRefreshCw,
  FiSave, FiAlertTriangle, FiToggleLeft, FiToggleRight,
  FiLock, FiCheck, FiInfo,
} = FiIcons;

// ── Permission definitions ────────────────────────────────────────────────────
const PERMISSION_GROUPS = [
  {
    group: 'Content',
    perms: [
      { key: 'moderation', label: 'Global Moderation',     desc: 'Approve/reject tributes and content' },
      { key: 'analytics',  label: 'View Analytics',         desc: 'Access site-wide analytics' },
      { key: 'memorials',  label: 'Memorial Management',    desc: 'Edit and manage all memorials' },
    ],
  },
  {
    group: 'Commerce',
    perms: [
      { key: 'merchants',  label: 'Merchant Management',    desc: 'Approve/reject merchant applications' },
      { key: 'payments',   label: 'Payment Settings',       desc: 'Configure payment gateways' },
    ],
  },
  {
    group: 'Platform',
    perms: [
      { key: 'webinars',   label: 'Webinar Integrations',   desc: 'Manage meeting platform configs' },
      { key: 'streaming',  label: 'Streaming Integrations', desc: 'Manage streaming platform configs' },
      { key: 'defaults',   label: 'Memorial Defaults',      desc: 'Set site-wide memorial defaults' },
      { key: 'scheduling', label: 'Appointment Scheduling', desc: 'Manage appointment queues' },
    ],
  },
];

const ALL_PERM_KEYS = PERMISSION_GROUPS.flatMap(g => g.perms.map(p => p.key));

const DEFAULT_PERMS = {
  moderation: true, analytics: true, memorials: true,
  merchants: false, payments: false,
  webinars: false, streaming: false, defaults: false, scheduling: true,
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const parsePerms = (raw) => { try { return JSON.parse(raw || '{}'); } catch { return {}; } };

// ── Sub-components ────────────────────────────────────────────────────────────
const Toggle = ({ active, onToggle, disabled }) => (
  <button type="button" onClick={onToggle} disabled={disabled}
    className={`w-10 h-5 rounded-full relative transition-colors flex-shrink-0 ${active ? 'bg-accent-gold' : 'bg-brand-dark'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${active ? 'right-0.5' : 'left-0.5'}`} />
  </button>
);

// ── Add Admin Modal ───────────────────────────────────────────────────────────
const AddAdminModal = ({ isOpen, onClose, onAdded }) => {
  const { user } = useAuth();
  const [form, setForm]     = useState({ email: '', name: '', notes: '' });
  const [perms, setPerms]   = useState({ ...DEFAULT_PERMS });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const togglePerm = (k) => setPerms(p => ({ ...p, [k]: !p[k] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return setError('Email is required');
    setSaving(true); setError('');
    try {
      const res = await writeFetch('create/site_admins_1741165000', {
        method: 'POST',
        body: JSON.stringify({
          id: crypto.randomUUID(),
          user_id: form.email.trim().toLowerCase(),
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          role: 'admin',
          permissions: JSON.stringify(perms),
          granted_by: user?.id || 'admin',
          is_active: 1,
          notes: form.notes.trim(),
        }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        onAdded(); onClose();
        setForm({ email: '', name: '', notes: '' });
        setPerms({ ...DEFAULT_PERMS });
      } else {
        setError(data.message || 'Failed to create admin');
      }
    } catch { setError('Network error. Try again.'); }
    setSaving(false);
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl border border-brand-dark my-4 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="bg-accent-navy text-white rounded-t-[40px] p-8 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-accent-gold" />
            </div>
            <div>
              <h3 className="font-serif text-2xl">Create Site Administrator</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">Assign admin role & permissions</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto min-h-0 custom-scrollbar">
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm">
              <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Email *</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                <input required type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full pl-11 pr-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Display Name</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                <input value={form.name} onChange={e => set('name', e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full pl-11 pr-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Permissions</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setPerms(Object.fromEntries(ALL_PERM_KEYS.map(k => [k, true])))}
                  className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-xl hover:bg-green-100 transition-colors">All</button>
                <button type="button" onClick={() => setPerms(Object.fromEntries(ALL_PERM_KEYS.map(k => [k, false])))}
                  className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded-xl hover:bg-red-100 transition-colors">None</button>
              </div>
            </div>
            <div className="space-y-3 pr-1">
              {PERMISSION_GROUPS.map(g => (
                <div key={g.group}>
                  <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1.5">{g.group}</p>
                  <div className="space-y-1.5">
                    {g.perms.map(perm => (
                      <div key={perm.key} className="flex items-center justify-between p-3 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold/40 transition-colors">
                        <div>
                          <p className="text-xs font-bold text-accent-navy">{perm.label}</p>
                          <p className="text-[10px] text-text-muted">{perm.desc}</p>
                        </div>
                        <Toggle active={!!perms[perm.key]} onToggle={() => togglePerm(perm.key)} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Internal Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
              placeholder="Reason for granting access…"
              className="w-full px-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-light resize-none" />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-[11px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-8 py-3 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg disabled:opacity-60">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SafeIcon icon={FiShield} className="w-4 h-4" />}
              {saving ? 'Creating…' : 'Create Admin'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// ── Edit Permissions Drawer ───────────────────────────────────────────────────
const EditPermissionsModal = ({ isOpen, admin, onClose, onSaved }) => {
  const [perms, setPerms]   = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => { if (admin) setPerms(parsePerms(admin.permissions)); }, [admin]);

  const togglePerm = (k) => setPerms(p => ({ ...p, [k]: !p[k] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await writeFetch(`update/site_admins_1741165000/${admin.id}`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: JSON.stringify(perms) }),
      });
      setSaved(true);
      setTimeout(() => { setSaved(false); onSaved(); onClose(); }, 800);
    } catch { /* silent */ }
    setSaving(false);
  };

  if (!isOpen || !admin) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg border border-brand-dark my-4 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="bg-accent-navy text-white rounded-t-[40px] p-7 flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-serif text-xl">Edit Permissions</h3>
            <p className="text-white/50 text-[10px] font-black tracking-widest mt-0.5">{admin.email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
        <div className="p-7 space-y-4 overflow-y-auto min-h-0 custom-scrollbar">
          {PERMISSION_GROUPS.map(g => (
            <div key={g.group}>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2">{g.group}</p>
              <div className="space-y-2">
                {g.perms.map(perm => (
                  <div key={perm.key} className="flex items-center justify-between p-3 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold/30 transition-colors">
                    <div>
                      <p className="text-xs font-bold text-accent-navy">{perm.label}</p>
                      <p className="text-[10px] text-text-muted">{perm.desc}</p>
                    </div>
                    <Toggle active={!!perms[perm.key]} onToggle={() => togglePerm(perm.key)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={onClose} className="px-6 py-3 rounded-2xl border-2 border-brand-dark text-[11px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all">Cancel</button>
            <button onClick={handleSave} disabled={saving || saved}
              className={`flex items-center gap-2 px-8 py-3 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all shadow-lg disabled:opacity-60 ${saved ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90'}`}>
              {saved ? <SafeIcon icon={FiCheck} className="w-4 h-4" /> : saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
              {saved ? 'Saved!' : saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ── Admin Card ────────────────────────────────────────────────────────────────
const AdminCard = ({ admin, onToggleActive, onEdit, onDelete, isSelf }) => {
  const perms       = parsePerms(admin.permissions);
  const activePerms = Object.values(perms).filter(Boolean).length;
  const isActive    = admin.is_active === 1 || admin.is_active === '1';

  return (
    <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-[28px] border-2 p-6 transition-all ${isActive ? 'border-brand-dark hover:border-accent-gold/40' : 'border-dashed border-brand-dark/40 opacity-60'}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-white font-bold text-base flex-shrink-0 ${isActive ? 'bg-accent-navy' : 'bg-gray-400'}`}>
            {(admin.name || admin.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-accent-navy text-sm">{admin.name || 'Unnamed Admin'}</p>
              {isSelf && <span className="text-[8px] px-2 py-0.5 bg-accent-gold/20 text-accent-gold border border-accent-gold/30 rounded-full font-black uppercase tracking-widest">You</span>}
              <span className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest border ${isActive ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                {isActive ? 'Active' : 'Suspended'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <SafeIcon icon={FiMail} className="w-3 h-3 text-text-muted" />
              <p className="text-[10px] text-text-muted font-mono">{admin.email}</p>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <SafeIcon icon={FiLock} className="w-3 h-3 text-accent-gold" />
              <p className="text-[10px] text-text-muted"><span className="font-bold text-accent-navy">{activePerms}</span>/{ALL_PERM_KEYS.length} permissions</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={() => onEdit(admin)} title="Edit permissions"
            className="p-2 rounded-xl border border-brand-dark hover:border-accent-gold hover:text-accent-gold transition-all text-text-muted">
            <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onToggleActive(admin)} disabled={isSelf} title={isActive ? 'Suspend' : 'Reactivate'}
            className={`p-2 rounded-xl border transition-all disabled:opacity-30 disabled:cursor-not-allowed ${isActive ? 'border-amber-200 text-amber-600 hover:bg-amber-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
            <SafeIcon icon={isActive ? FiToggleRight : FiToggleLeft} className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(admin)} disabled={isSelf} title="Delete admin"
            className="p-2 rounded-xl border border-red-200 text-red-400 hover:bg-red-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed">
            <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Permission pills */}
      <div className="flex flex-wrap gap-1">
        {PERMISSION_GROUPS.flatMap(g => g.perms).map(perm => (
          <span key={perm.key}
            className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest border ${perms[perm.key] ? 'bg-accent-navy/5 border-accent-navy/20 text-accent-navy' : 'bg-gray-50 border-gray-200 text-gray-400 line-through'}`}>
            {perm.label}
          </span>
        ))}
      </div>

      {admin.notes && (
        <p className="mt-3 text-[10px] text-text-muted bg-brand-light rounded-xl px-3 py-2 italic">📝 {admin.notes}</p>
      )}
      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-text-muted">
        <SafeIcon icon={FiClock} className="w-3 h-3" />
        Added {new Date(admin.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
    </motion.div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const UserManagement = () => {
  const { user, permissions } = useAuth();
  const isSuperAdmin = permissions?.isSuperAdmin?.();

  const [admins, setAdmins]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await writeFetch('read/site_admins_1741165000?sort=created_at&order=desc&limit=100');
      const data = await res.json();
      setAdmins(Array.isArray(data.data) ? data.data : []);
    } catch { setAdmins([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleToggleActive = async (admin) => {
    const newVal = (admin.is_active === 1 || admin.is_active === '1') ? 0 : 1;
    await writeFetch(`update/site_admins_1741165000/${admin.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: newVal }),
    });
    fetchAdmins();
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin account for ${admin.email}? This cannot be undone.`)) return;
    await writeFetch(`delete/site_admins_1741165000/${admin.id}`, { method: 'DELETE' });
    fetchAdmins();
  };

  const filtered = admins.filter(a => {
    const matchSearch = !search ||
      a.email?.toLowerCase().includes(search.toLowerCase()) ||
      a.name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === 'all' ||
      (filter === 'active'    && (a.is_active === 1 || a.is_active === '1')) ||
      (filter === 'suspended' && (a.is_active === 0 || a.is_active === '0'));
    return matchSearch && matchFilter;
  });

  const activeCount    = admins.filter(a => a.is_active === 1 || a.is_active === '1').length;
  const suspendedCount = admins.length - activeCount;

  return (
    <div className="space-y-8">

      {/* Header Banner */}
      <div className="bg-gradient-to-r from-accent-navy to-accent-navy/80 text-white rounded-[32px] p-8 flex items-center gap-6 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center flex-shrink-0 z-10">
          <SafeIcon icon={FiUsers} className="w-8 h-8 text-accent-gold" />
        </div>
        <div className="z-10 flex-1">
          <h2 className="font-serif text-2xl mb-1">User Management</h2>
          <p className="text-white/60 text-sm font-light">
            View, create, and manage Site Administrator accounts. Toggle granular permissions per admin to control exactly what each administrator can access.
          </p>
        </div>
        {!isSuperAdmin && (
          <div className="z-10 flex-shrink-0 bg-white/10 rounded-2xl p-4 border border-white/10 hidden md:block">
            <div className="flex items-center gap-2 text-white/70 text-xs">
              <SafeIcon icon={FiInfo} className="w-4 h-4 text-accent-gold flex-shrink-0" />
              <p>Super Admins can <br />manage your account</p>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-6">
        {[
          { label: 'Total Admins',     value: admins.length,    color: 'text-accent-navy',  bg: 'bg-accent-navy/5'  },
          { label: 'Active',           value: activeCount,      color: 'text-green-600',    bg: 'bg-green-50'       },
          { label: 'Suspended',        value: suspendedCount,   color: 'text-amber-600',    bg: 'bg-amber-50'       },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-[24px] border border-brand-dark p-6 text-center`}>
            <p className={`text-3xl font-serif font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'active', 'suspended'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${filter === f ? 'bg-accent-navy text-white border-accent-navy' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}>
              {f}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search admins…"
              className="pl-10 pr-4 py-2.5 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm w-48" />
          </div>
          <button onClick={fetchAdmins}
            className="p-2.5 bg-white border-2 border-brand-dark rounded-2xl hover:border-accent-gold transition-all text-text-muted hover:text-accent-gold">
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          </button>
          <button onClick={() => setIsAddOpen(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg">
            <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Admin
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-12 h-12 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
          <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading administrators…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-[40px] border-2 border-dashed border-brand-dark">
          <div className="w-20 h-20 bg-brand-light rounded-3xl flex items-center justify-center mx-auto mb-6 border border-brand-dark/50">
            <SafeIcon icon={FiUsers} className="w-10 h-10 text-text-muted/30" />
          </div>
          <h4 className="text-2xl font-serif text-accent-navy mb-3">No Administrators Found</h4>
          <p className="text-text-muted font-light mb-8">
            {search ? 'No admins match your search.' : 'Create the first administrator using the button above.'}
          </p>
          {!search && (
            <button onClick={() => setIsAddOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg">
              <SafeIcon icon={FiPlus} className="w-4 h-4" /> Create First Admin
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AnimatePresence>
            {filtered.map(admin => (
              <AdminCard
                key={admin.id}
                admin={admin}
                isSelf={admin.email?.toLowerCase() === user?.email?.toLowerCase()}
                onToggleActive={handleToggleActive}
                onEdit={a => setEditAdmin(a)}
                onDelete={handleDelete}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      <AddAdminModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onAdded={fetchAdmins} />
      <EditPermissionsModal isOpen={!!editAdmin} admin={editAdmin} onClose={() => setEditAdmin(null)} onSaved={fetchAdmins} />
    </div>
  );
};

export default UserManagement;